import $ from "jquery";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";

const PAYMENT_TYPES = [
  "Online",
  "DD",
  "Cheque",
  "Damage",
  "Incentive",
  "Rebate",
  "Sales Return",
  "Discount",
  "Settlement",
];

/**
 * The legacy page carried a stock check, but it fed the buttons a hardcoded
 * stock of 500000, so the check never actually fired and a demand could always
 * exceed stock. Flip this to true to enforce the real figure from
 * /fg-with-stock instead.
 */
const ENFORCE_STOCK_LIMIT = false;

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const Create = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({ lock_demand: false, payment_amount: "" });
  const [error, setError] = useState({});
  const [product, setProduct] = useState([]);
  const [distributor, setDistributor] = useState([]);
  const [sr, setSr] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
    $("body").toggleClass("collapse-menu");
    $("body").toggleClass("show-left");
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [resProduct, resDistributor, resDemandNo] = await Promise.all([
        Http.get("/fg-with-stock"),
        Http.get("/distributor2"),
        Http.get("/last-code-increment-by-one/demands/receipt_no/DM"),
      ]);
      setProduct(resProduct.data);
      setDistributor(resDistributor.data);

      const now = new Date();
      setInputs((values) => ({
        ...values,
        receipt_no: resDemandNo.data,
        date: now.toISOString().slice(0, 10),
        order_date: now.toISOString().slice(0, 10),
        time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
      }));
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const totalOf = (rows) => rows.reduce((acc, row) => acc + toNumber(row.line_total), 0);

  const isRetail = () => String(inputs.business_type || "").toLowerCase() === "retail";

  /**
   * Retail distributors may not demand more than they are paying for. Mirrors
   * the legacy dueAmountCheck(): other business types are never blocked.
   */
  const dueAmountCheck = (rows, paymentAmount) => {
    if (!isRetail()) return true;

    const total = totalOf(rows);
    const payment = toNumber(paymentAmount);

    if (payment < 1 && total > 0) {
      Swal.fire("Warning!", "পেমেন্ট এমাউন্ট 0 হতে পারবে না !", "warning");
      return false;
    }
    if (total > payment + 500) {
      Swal.fire("Invalid!", "Total price is not within the payment amount ±500.", "warning");
      return false;
    }

    return true;
  };

  /**
   * Remaining amount is what the distributor can still spend, and due_amount is
   * what the demand leaves owing. Both follow the legacy formulas.
   */
  const recalc = (rows, overrides = {}) => {
    setInputs((values) => {
      const next = { ...values, ...overrides };
      const total = totalOf(rows);
      const payment = toNumber(next.payment_amount);
      const previousDue = toNumber(next.previous_due);

      return {
        ...next,
        total_price: total,
        remaining_amount: payment + previousDue - total,
        due_amount: total - payment,
      };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs((values) => ({ ...values, [name]: type === "checkbox" ? checked : value }));
  };

  const handlePaymentChange = (e) => {
    recalc(list, { payment_amount: e.target.value });
  };

  // The distributor's running balance is read fresh whenever the date moves,
  // the same way the legacy page re-queried it.
  const handleDateChange = async (e) => {
    const { value } = e.target;
    setInputs((values) => ({ ...values, date: value }));

    if (!inputs.distributor_id) return;

    try {
      const due = await Http.get(`/demand-distributor-due/${inputs.distributor_id}`);
      recalc(list, { date: value, previous_due: due.data.previous_due || 0 });
    } catch (e2) {
      console.error("Due fetch error:", e2);
    }
  };

  const selectHandleChange = async (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    const { value } = selectedOption;

    setInputs((values) => ({ ...values, [name]: value }));

    try {
      const res = await Http.get(`/distributor/${value}`);
      const listInfo = [];

      // if (res.data.business_type === "ho") {
        const dataInfo = await Http.get(`/region_area_base_users/${value}`);
        listInfo.push(...dataInfo.data.map((item) => ({ id: item.id, name: item.name })));
      // } else {
      //   for (const key in res.data.sr_base_info) {
      //     const base = res.data.sr_base_info[key].base;
      //     if (base && base.users && base.users.length) {
      //       base.users.forEach((user) => {
      //         if (user.status === "active") listInfo.push({ id: user.id, name: user.name });
      //       });
      //     }
      //   }
      // }
      setSr(listInfo);

      const due = await Http.get(`/demand-distributor-due/${value}`);
      recalc(list, {
        business_type: res.data.business_type,
        previous_due: due.data.previous_due || 0,
      });
    } catch (e) {
      console.error("Distributor fetch error:", e);
    }
  };

  /**
   * Trade offer for a line, from the offer attached to the product.
   */
  const tradeOfferQty = (row, qty) => {
    if (!row.trade_offer) return 0;

    const offerAt = toNumber(row.trade_offer.qty);
    const offerQty = toNumber(row.trade_offer.offer_qty);

    if (offerAt <= 0 || offerQty <= 0) return 0;

    return Math.floor(toNumber(qty) / offerAt) * offerQty;
  };

  /**
   * Rebuild one line from a new qty/rate, keeping trade offer and totals in step.
   */
  const buildRow = (row, changes) => {
    const next = { ...row, ...changes };
    next.to_qty = tradeOfferQty(next, next.qty);
    next.total_qty = toNumber(next.qty) + toNumber(next.to_qty);
    next.line_total = toNumber(next.qty) * toNumber(next.rate);
    return next;
  };

  const addMoreList = async (item) => {
    // Both gates come straight from the legacy addProduct handler.
    if (!inputs.distributor_id) {
      Swal.fire("Warning!", "Please select a distributor first.", "warning");
      return;
    }
    if (toNumber(inputs.payment_amount) < 1) {
      Swal.fire("Warning!", "Payment amount should not be 0.", "warning");
      return;
    }

    // Adding a product already on the sheet bumps its quantity instead.
    const existingIndex = list.findIndex((row) => row.id === item.id);
    if (existingIndex > -1) {
      const rows = [...list];
      rows[existingIndex] = buildRow(rows[existingIndex], { qty: toNumber(rows[existingIndex].qty) + 1 });
      setList(rows);
      recalc(rows);
      return;
    }

    try {
      const response = await Http.get(`/product/${item.id}`);
      const p = response.data;
      const stock = toNumber(item.stockQty);

      const row = {
        id: p.id,
        code: p.code,
        name: p.name,
        pack_size: p.pack_size || 0,
        stock: stock,
        trade_offer: p.trade_offer || null,
        // A new line starts empty; the legacy qty input was seeded to 0.
        qty: 0,
        rate: p.price || 0,
        to_qty: 0,
        total_qty: 0,
        line_total: 0,
      };

      const rows = [...list, row];
      setList(rows);
      recalc(rows);
    } catch (e) {
      Swal.fire("Error!", "Failed to add product.", "error");
    }
  };

  const removeList = (index) => {
    const rows = list.filter((_, i) => i !== index);
    setList(rows);
    recalc(rows);
  };

  const handleQtyChange = (e, index) => {
    const { value } = e.target;
    const rows = [...list];
    let qty = value;

    if (ENFORCE_STOCK_LIMIT && toNumber(qty) > toNumber(rows[index].stock)) {
      Swal.fire("Warning!", `You have ${rows[index].stock} items left in the stock.`, "warning");
      qty = rows[index].stock;
    }

    rows[index] = buildRow(rows[index], { qty });

    // The check runs against what the sheet would become, and a failure zeroes
    // the line, exactly as the legacy page did.
    if (!dueAmountCheck(rows, inputs.payment_amount)) {
      rows[index] = buildRow(rows[index], { qty: 0 });
    }

    setList(rows);
    recalc(rows);
  };

  const handleRateChange = (e, index) => {
    const rows = [...list];
    rows[index] = buildRow(rows[index], { rate: e.target.value });

    if (!dueAmountCheck(rows, inputs.payment_amount)) {
      rows[index] = buildRow(rows[index], { qty: 0 });
    }

    setList(rows);
    recalc(rows);
  };

  // Digits and a single decimal point only, like the legacy numOnly binding.
  const numOnly = (e) => {
    if (!/[0-9.]/.test(e.key) || (e.key === "." && String(e.target.value).includes("."))) {
      e.preventDefault();
    }
  };

  const formatOnBlur = (e, index, field) => {
    const rows = [...list];
    rows[index][field] = toNumber(rows[index][field]).toFixed(2);
    setList(rows);
  };

  const formSubmit = () => {
    if (list.length < 1) {
      Swal.fire("Warning!", "Please add a few products to process the demand.", "warning");
      return;
    }

    const total = totalOf(list);
    const payment = toNumber(inputs.payment_amount);

    // The ±500 window is checked for every distributor on submit.
    if (total < payment - 500 || total > payment + 500) {
      Swal.fire("Invalid!", "Total price is not within the payment amount ±500.", "warning");
      return;
    }

    const payload = {
      ...inputs,
      total_price: total,
      due_amount: total - payment,
      items: list.map((row) => ({
        id: row.id,
        pack_size: row.pack_size,
        qty: row.qty,
        inv_qty: row.total_qty,
        rate: row.rate,
      })),
    };

    Http.post("/demand", payload)
      .then((res) => {
        navigate("/demand-sheet", { state: { message: res.data.message, type: "success" } });
      })
      .catch((e) => {
        setError(e.response?.data?.errors || {});
        if (e.response?.data?.message && !e.response?.data?.errors) {
          Swal.fire("Failed!", e.response.data.message, "error");
        }
      });
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  const cols = [
    { field: "name", header: "Item" },
    { field: "stockQty", header: "Stock" },
  ];

  const actionTemplate = (item) => (
    <td className="text-right">
      <button className="btn btn-success btn-sm" onClick={() => addMoreList(item)}>
        <i className="fa fa-plus"></i>
      </button>
    </td>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Create Demand Sheet</h5>
        </div>
        <div className="am-pagebody row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-8">
            <h6 className="card-body-title mg-sm-b-30 text-center">New Demand</h6>
            <hr />

            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg">
                    <label>
                      Demand No <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="receipt_no"
                      value={inputs.receipt_no || ""}
                      onChange={handleChange}
                    />
                    {displayError("receipt_no")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Demand Date <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date || ""}
                      onChange={handleDateChange}
                    />
                    {displayError("date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Order Date <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="order_date"
                      value={inputs.order_date || ""}
                      onChange={handleChange}
                    />
                    {displayError("order_date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Time <i className="fa fa-star required"></i>
                    </label>
                    <input type="time" className="form-control" name="time" value={inputs.time || ""} onChange={handleChange} />
                    {displayError("time")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>
                      Distributor <i className="fa fa-star required"></i>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      name="distributor_id"
                      options={distributor}
                      onChange={selectHandleChange}
                    />
                    {displayError("distributor_id")}
                  </div>
                  <div className="col-lg">
                    <label>Sales Person</label>
                    <select className="form-control" name="sr_id" value={inputs.sr_id || ""} onChange={handleChange}>
                      <option value="">Select SR</option>
                      {Array.from(sr).map((item) => (
                        <option value={item.id} key={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {displayError("sr_id")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="payment_date"
                      value={inputs.payment_date || ""}
                      onChange={handleChange}
                    />
                    {displayError("payment_date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Payment Amount <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="payment_amount"
                      value={inputs.payment_amount || ""}
                      onChange={handlePaymentChange}
                      onKeyPress={numOnly}
                    />
                    {displayError("payment_amount")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Payment Type <i className="fa fa-star required"></i>
                    </label>
                    <select
                      className="form-control"
                      name="payment_type"
                      value={inputs.payment_type || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Payment Type</option>
                      {PAYMENT_TYPES.map((type) => (
                        <option value={type} key={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {displayError("payment_type")}
                  </div>
                  <div className="col-lg">
                    <label>Lock Demand</label>
                    <div className="mg-t-10">
                      <input type="checkbox" name="lock_demand" checked={!!inputs.lock_demand} onChange={handleChange} />
                      <span className="ms-2">Lock after creating</span>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Notes</label>
                    <textarea
                      className="form-control"
                      name="notes"
                      value={inputs.notes || ""}
                      onChange={handleChange}
                      placeholder="Insert note (if any)"
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="card-solid">
                    <div className="card-header with-border text-center">
                      <h5 className="card-title mb-0">
                        Items in the Demand <i className="fa fa-star required"></i>
                      </h5>
                    </div>
                    <div className="card-body p-0">
                      <table className="table table-sm table-bordered table-striped mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Code</th>
                            <th>Product</th>
                            <th>Crtn Size</th>
                            <th>Qty</th>
                            <th>Rate</th>
                            <th>Total Qty</th>
                            <th>Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.length < 1 && (
                            <tr>
                              <td colSpan={8}>
                                <p style={{ color: "red" }} className="text-center mt-3">
                                  Please add a few products to process the demand
                                </p>
                              </td>
                            </tr>
                          )}

                          {list.map((x, i) => (
                            <tr key={x.id}>
                              <td>
                                <button className="btn btn-danger btn-sm" onClick={() => removeList(i)}>
                                  <i className="fa fa-trash"></i>
                                </button>
                              </td>
                              <td>{x.code}</td>
                              <td>{x.name}</td>
                              <td>{x.pack_size}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="qty"
                                  value={x.qty}
                                  onChange={(e) => handleQtyChange(e, i)}
                                  onKeyPress={numOnly}
                                  onBlur={(e) => formatOnBlur(e, i, "qty")}
                                />
                                {displayError("items." + i + ".qty")}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="rate"
                                  value={x.rate}
                                  onChange={(e) => handleRateChange(e, i)}
                                  onKeyPress={numOnly}
                                  onBlur={(e) => formatOnBlur(e, i, "rate")}
                                />
                                {displayError("items." + i + ".rate")}
                              </td>
                              <td>
                                <input type="number" className="form-control" value={x.total_qty || 0} disabled />
                              </td>
                              <td>
                                <input type="number" className="form-control" value={x.line_total || 0} disabled />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={7} className="text-right" style={{ lineHeight: "40px" }}>
                              Total Price
                            </td>
                            <td>
                              <input type="text" className="form-control" value={inputs.total_price || 0} disabled />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={7} className="text-right" style={{ lineHeight: "40px" }}>
                              Previous Due
                            </td>
                            <td>
                              <input type="text" className="form-control" value={inputs.previous_due || 0} disabled />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={7} className="text-right" style={{ lineHeight: "40px" }}>
                              Remaining Amount
                            </td>
                            <td>
                              <input type="text" className="form-control" value={inputs.remaining_amount || 0} disabled />
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button className="btn btn-primary btn-block mg-b-10" onClick={formSubmit}>
                      <i className="fa fa-send mg-r-10"></i> Process Demand
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="card pd-20 pd-sm-40 col-3" style={{ backgroundColor: "#ffffffc7" }}>
            <h6 className="card-body-title mg-sm-b-30 text-center">Available Items</h6>
            <hr />
            <ReactDataTable cols={cols} receiveData={product} isLoading={loading} actionTemplate={actionTemplate} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create;
